// ─────────────────────────────────────────────────────────────────────────────
// 🔑 THE DASHBOARD'S MAIN CARD, ONE PER STAGE (Jesse/Hannah, 2026-11-02 — the Home Shopping page redesign, approved).
// The guided flow's white step card (GuidedStepCard's card), its title and line (GuidedDocumentsStep's 16/800 and
// 13.5/1.6), its light-blue panel (Step5AskCard ASK_BOX) and its primary button. Every figure comes from the status read's
// `journey` (backend utils/borrowerJourney); every word from constants/journeySteps JOURNEY_COPY (approved).
//   Home Shopping  — "You’re approved up to $[live decision maximum]", today's next-step sentence, item 10's sentence in
//                    the light-blue panel ("down payment" plain text), "View your SmartEstimate", "Browse homes"
//                    (full width on a phone, normal width on a computer).
//                    🔑 "View your SmartEstimate" opens the borrower's SIGNED SmartEstimate PDF (journey
//                    .smartestimate_document_id — their own signed row, backend utils/borrowerJourney) in the Documents
//                    viewer; NEVER the simulator (Hannah's review 2026-11-03 item 3). A joint co-borrower gets the
//                    primary's signed copy (the viewer link lets that file's co-borrower open it). No signed PDF → no link.
//   Under contract — "Under contract · [address] · $[contract price]"; during the option period, "Your option period
//                    ends [Mon D] at 5:00 p.m." (5 p.m. where the home is — no zone shown).
//   Closing        — "Closing · [address] · $[price]" and "Your closing is on [Weekday, Mon D]."
// Renders nothing at any other stage.
// ─────────────────────────────────────────────────────────────────────────────
import React from 'react';
import { journeyStepFor, JOURNEY_COPY, showOptionEnds, DASHBOARD_GREETING, headlineWithName } from '../../constants/journeySteps';

const TITLE = { fontSize: 16, fontWeight: 800, color: '#000' };
const LINE = { fontSize: 13.5, color: '#000', marginTop: 4, lineHeight: 1.6 };
const ASK_BOX = { backgroundColor: '#eff3fe', border: '1px solid #bfdbfe' };   // components/guided/Step5AskCard ASK_BOX
const PRIMARY = { padding: '14px 18px', fontSize: 15, fontWeight: 800, borderRadius: 12, border: 'none', background: '#3a6ad9', color: '#fff', cursor: 'pointer' };

export function MainCardFrame({ children, testId }) {
  return (
    <div data-testid={testId} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="px-6 sm:px-8 pt-8 pb-6">{children}</div>
    </div>
  );
}

export default function JourneyMainCard({ status, onBrowse, onViewSmartEstimate, onPrepareSmartEstimate = undefined, openError = null,
  greeting = DASHBOARD_GREETING, firstName = '' }) {
  // 🔑 Greeting option 'c' (constants/journeySteps DASHBOARD_GREETING): the first name leads the headline. Off by default.
  const head = (h) => (greeting === 'c' ? headlineWithName(firstName, h) : h);
  const j = journeyStepFor(status);
  if (!j) return null;
  const stage = status.loan_stage;
  const facts = status.journey || {};

  if (stage === 'home_shopping') {
    return (
      <MainCardFrame testId="journey-main-card">
        <div data-stage="home_shopping">
          {facts.approved_max ? <div data-testid="journey-main-title" style={TITLE}>{head(JOURNEY_COPY.approved(facts.approved_max))}</div> : null}
          <div data-testid="journey-main-line" style={LINE}>{JOURNEY_COPY.homeShoppingNext}</div>
          <div data-testid="journey-down-payment-panel" style={{ ...ASK_BOX, borderRadius: 10, padding: '10px 12px', marginTop: 12 }}>
            <div style={{ fontSize: 13.5, color: '#000', lineHeight: 1.6, overflowWrap: 'anywhere' }}>{JOURNEY_COPY.downPaymentReady}</div>
          </div>
          {onViewSmartEstimate && facts.smartestimate_document_id ? (
            <div style={{ marginTop: 12 }}>
              <button type="button" data-testid="journey-view-smartestimate" data-document-id={facts.smartestimate_document_id}
                // The link is minted on the way to the tap, as Documents' View does (utils/openDocument prepareDocument).
                onPointerEnter={() => onPrepareSmartEstimate && onPrepareSmartEstimate(facts.smartestimate_document_id)}
                onFocus={() => onPrepareSmartEstimate && onPrepareSmartEstimate(facts.smartestimate_document_id)}
                onTouchStart={() => onPrepareSmartEstimate && onPrepareSmartEstimate(facts.smartestimate_document_id)}
                onClick={() => onViewSmartEstimate(facts.smartestimate_document_id)}
                style={{ padding: 0, background: 'none', border: 'none', color: '#3a6ad9', fontSize: 13.5, fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}>
                {JOURNEY_COPY.viewSmartEstimate}
              </button>
              {openError ? <div data-testid="journey-view-smartestimate-error" role="alert" style={{ fontSize: 13, color: '#b91c1c', marginTop: 6 }}>{openError}</div> : null}
            </div>
          ) : null}
          <button type="button" data-testid="journey-browse-homes" onClick={onBrowse} className="guided-primary w-full sm:w-auto" style={{ ...PRIMARY, marginTop: 16 }}>
            {JOURNEY_COPY.browse}
          </button>
        </div>
      </MainCardFrame>
    );
  }

  if (j.step === 3) {
    return (
      <MainCardFrame testId="journey-main-card">
        <div data-stage={stage}>
          <div data-testid="journey-main-title" style={TITLE}>{head(JOURNEY_COPY.underContract(facts.property_address, facts.contract_price))}</div>
          {showOptionEnds(status) && (
            <div data-testid="journey-option-ends" style={LINE}>{JOURNEY_COPY.optionEnds(facts.option_period_end)}</div>
          )}
        </div>
      </MainCardFrame>
    );
  }

  if (j.step === 4) {
    return (
      <MainCardFrame testId="journey-main-card">
        <div data-stage={stage}>
          <div data-testid="journey-main-title" style={TITLE}>{head(JOURNEY_COPY.closingTitle(facts.property_address, facts.contract_price))}</div>
          {facts.closing_date && <div data-testid="journey-closing-on" style={LINE}>{JOURNEY_COPY.closingOn(facts.closing_date)}</div>}
        </div>
      </MainCardFrame>
    );
  }
  return null;
}
